import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  isAaaGameInformationAvailabilityFormValid,
  isAaaGameInformationConfigurationsFormValid,
  isAaaGameInformationDetailsFormValid,
  isAaaGameInformationDistributionInformationFormValid,
  isAaaGameInformationGameExpectationsValid,
  isAaaGameInformationSupplementaryDataFormValid,
  isAaaGameInformationTechnicalCapabilitiesFormValid,
  isAaaGameInformationUgcFormValid,
  isAaaGameInformationXboxNetworkFormValid,
} from "../../helpers/formValidation";
import {
  AaaGameInformationAvailabilityDataType,
  AaaGameInformationConceptDetailsDataType,
  AaaGameInformationConfigurationsDataType,
  AaaGameInformationDistributionInformationDataType,
  AaaGameInformationGameExpectationsDataType,
  AaaGameInformationSupplementaryDataType,
  AaaGameInformationTechnicalCapabilitiesDataType,
  AaaGameInformationUgcDataType,
  AaaGameInformationXboxNetworkDataType,
} from "../../models/forms";
import { AaaApplicationStep } from "../../models/status";
import {
  AaaGameInformationFormId,
  AaaGameInformationSubFormsId,
} from "../../pages/AaaGameInformationPage/AaaGameInformationPageConstants";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import { getAaaApplicationStep, getSubFormData } from "../../redux/selectors";

const stepPercentageValue = 10;
const increasePercentageIfValid = (isFormValid: boolean, currentPercentage: number): number =>
  isFormValid ? currentPercentage + stepPercentageValue : currentPercentage;

export const useAaaPercentage = () => {
  const [percentage, setPercentage] = useState(0);
  const currentStep = useSelector(getAaaApplicationStep) ?? AaaApplicationStep.Unset;

  // AAA Game Information sub form data
  const gameInfoConceptSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.ConceptDetails)
  ) as AaaGameInformationConceptDetailsDataType;
  const gameInfoDistributionInformationSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(
      state,
      AaaGameInformationFormId,
      AaaGameInformationSubFormsId.DistributionInformation
    )
  ) as AaaGameInformationDistributionInformationDataType;
  const gameInfoGameExpectationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.GameExpectations)
  ) as AaaGameInformationGameExpectationsDataType;
  const gameInfoAvailabilitiesSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.Availability)
  ) as AaaGameInformationAvailabilityDataType;
  const gameInfoTechnicalCapabilitiesSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(
      state,
      AaaGameInformationFormId,
      AaaGameInformationSubFormsId.TechnicalCapabilities
    )
  ) as AaaGameInformationTechnicalCapabilitiesDataType;
  const gameInfoConfigurationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.Configurations)
  ) as AaaGameInformationConfigurationsDataType;
  const gameInfoXboxNetworkSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.XboxNetwork)
  ) as AaaGameInformationXboxNetworkDataType;
  const gameInfoUgcSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.Ugc)
  ) as AaaGameInformationUgcDataType;
  const gameInfoSupplementaryDataSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.SupplementaryData)
  ) as AaaGameInformationSupplementaryDataType;

  useEffect(() => {
    const calculatePercentage = async () => {
      if (
        currentStep === AaaApplicationStep.ApplicationComplete ||
        currentStep === AaaApplicationStep.ApplicationRejected ||
        currentStep === AaaApplicationStep.ApplicationIsBeingReviewed ||
        currentStep === AaaApplicationStep.ApplicationReadyForReview
      ) {
        setPercentage(100);
        return;
      }

      let tempPercentage = 0;
      if (currentStep === AaaApplicationStep.Unset) {
        setPercentage(tempPercentage);
      }

      // Game Information
      const isGameInfoConceptFormValid =
        (await isAaaGameInformationDetailsFormValid(gameInfoConceptSubFormData)) &&
        gameInfoConceptSubFormData.isStarted;
      const isGameInfoDistributionInformationFormValid =
        (await isAaaGameInformationDistributionInformationFormValid(
          gameInfoDistributionInformationSubFormData
        )) && gameInfoDistributionInformationSubFormData.isStarted;
      const isGameInfoGameExpectationsFormValid =
        (await isAaaGameInformationGameExpectationsValid(gameInfoGameExpectationsSubFormData)) &&
        gameInfoGameExpectationsSubFormData.isStarted;
      const isGameInfoAvailabilityFormValid =
        (await isAaaGameInformationAvailabilityFormValid(gameInfoAvailabilitiesSubFormData)) &&
        gameInfoAvailabilitiesSubFormData.isStarted;
      const isGameInfoTechnicalCapabilitiesFormValid =
        (await isAaaGameInformationTechnicalCapabilitiesFormValid(
          gameInfoTechnicalCapabilitiesSubFormData
        )) && gameInfoTechnicalCapabilitiesSubFormData.isStarted;
      const isGameInfoConfigurationsFormValid =
        (await isAaaGameInformationConfigurationsFormValid(gameInfoConfigurationsSubFormData)) &&
        gameInfoConfigurationsSubFormData.isStarted;
      const isGameInfoXboxNetworkFormValid =
        (await isAaaGameInformationXboxNetworkFormValid(gameInfoXboxNetworkSubFormData)) &&
        gameInfoXboxNetworkSubFormData.isStarted;
      const isGameInfoUgcFormValid =
        (await isAaaGameInformationUgcFormValid(gameInfoUgcSubFormData)) &&
        gameInfoUgcSubFormData.isStarted;
      const isGameInfoSupplementaryDataFormValid =
        (await isAaaGameInformationSupplementaryDataFormValid(
          gameInfoSupplementaryDataSubFormData
        )) && gameInfoSupplementaryDataSubFormData.isStarted;
      tempPercentage = increasePercentageIfValid(isGameInfoConceptFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(
        isGameInfoDistributionInformationFormValid,
        tempPercentage
      );
      tempPercentage = increasePercentageIfValid(
        isGameInfoGameExpectationsFormValid,
        tempPercentage
      );
      tempPercentage = increasePercentageIfValid(isGameInfoAvailabilityFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(
        isGameInfoTechnicalCapabilitiesFormValid,
        tempPercentage
      );
      tempPercentage = increasePercentageIfValid(isGameInfoConfigurationsFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(isGameInfoXboxNetworkFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(isGameInfoUgcFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(
        isGameInfoSupplementaryDataFormValid,
        tempPercentage
      );
      setPercentage(tempPercentage);
    };

    calculatePercentage();
  }, [
    currentStep,
    gameInfoConceptSubFormData,
    gameInfoGameExpectationsSubFormData,
    gameInfoConfigurationsSubFormData,
    gameInfoTechnicalCapabilitiesSubFormData,
    gameInfoXboxNetworkSubFormData,
    gameInfoDistributionInformationSubFormData,
    gameInfoAvailabilitiesSubFormData,
    gameInfoUgcSubFormData,
    gameInfoSupplementaryDataSubFormData,
  ]);

  return percentage;
};
